import React, { useState, useEffect } from 'react';
import { SignalIcon, FolderIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import { getCustomerProjects, getCustomerChannels } from '../utils/api';

const CustomerProjectsChannelsModal = ({ isOpen, onClose, customer }) => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && customer) {
      loadData();
    }
  }, [isOpen, customer]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, channelsRes] = await Promise.all([
        getCustomerProjects(customer.id),
        getCustomerChannels(customer.id)
      ]);
      setProjects(projectsRes.data);
      setChannels(channelsRes.data);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${customer.name} - Projects & Channels`}
      size="lg"
      footer={
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--color-border-primary)', paddingBottom: '0.5rem' }}>
        <button
          className={`btn btn-sm ${activeTab === 'projects' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setActiveTab('projects')}
        >
          <FolderIcon style={{ width: 16, height: 16, marginRight: '0.5rem' }} />
          Products ({projects.length})
        </button>
        <button
          className={`btn btn-sm ${activeTab === 'channels' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setActiveTab('channels')}
        >
          <SignalIcon style={{ width: 16, height: 16, marginRight: '0.5rem' }} />
          Channels ({channels.length})
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div className="spinner" />
          <span style={{ marginLeft: '0.5rem' }}>Loading...</span>
        </div>
      ) : (
        <>
          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div>
              {projects.length > 0 ? (
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Project Code</th>
                        <th>Project Name</th>
                        <th>Country</th>
                        <th>Product</th>
                        <th style={{ textAlign: 'right' }}>Channels</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => (
                        <tr key={project.id}>
                          <td><strong>{project.code}</strong></td>
                          <td>{project.name}</td>
                          <td>{project.country || '-'}</td>
                          <td>{project.product || '-'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <span className="badge badge-info">{project.channel_count}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-tertiary)' }}>
                  <FolderIcon style={{ width: 48, height: 48, margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No products assigned to this customer yet.</p>
                  <p style={{ fontSize: '0.85rem' }}>Assign products via Channel Management.</p>
                </div>
              )}
            </div>
          )}

          {/* Channels Tab */}
          {activeTab === 'channels' && (
            <div>
              {channels.length > 0 ? (
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Channel Name</th>
                        <th>Price Type</th>
                        <th>Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channels.map((channel) => (
                        <tr key={channel.id}>
                          <td><strong>{channel.channel_name}</strong></td>
                          <td>{channel.price_type || '-'}</td>
                          <td>
                            {channel.project_name ? (
                              <span style={{ color: 'var(--color-accent-primary)' }}>
                                {channel.project_name}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-tertiary)' }}>
                  <SignalIcon style={{ width: 48, height: 48, margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No channels assigned to this customer yet.</p>
                  <p style={{ fontSize: '0.85rem' }}>Assign channels via Channel Management.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default CustomerProjectsChannelsModal;
